import * as React from 'react';
import {useCallback, useRef, useState} from 'react';
import {CircularProgress, IconButton, Typography} from '@mui/material';
import {Close, InsertDriveFileOutlined, PhotoCameraOutlined} from '@mui/icons-material';
import {useAppDispatch} from '../../store';
import {useAppTranslation} from '../../services/i18n';
import {addMessage} from 'store/localApp';
import {uploadFile} from '../../helpers/upload';
import {decimalNumber} from '../../helpers/number';

export interface ApprovalFileValue {
    id: string;
    file_name: string;
    size?: number;
    /** present for already stored files and for fresh uploads, so both can be opened */
    download_url?: string;
}

// mirrors ApprovalRequest::ALLOWED_FILE_TYPES / MAX_FILE_SIZE on the backend
const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

/** `ApprovalRequest::DRAFT_ALLOWED_FILE_TYPES` -- documents only, no photos. */
export const DRAFT_ACCEPTED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const fileSize = (size?: number) => {
    if (!size && size !== 0) {
        return '';
    }
    return size < 1024 * 1024
        ? decimalNumber(size / 1024, 0, 0) + ' kB'
        : decimalNumber(size / (1024 * 1024), 1, 1) + ' MB';
};

export const ApprovalFileField = ({label, hint, required, multiple, value, onChange, error, maxFiles, usedFiles = 0,
    acceptedTypes = ACCEPTED_MIME_TYPES}: {
    label: string,
    hint?: string,
    /** marks the label the way a required text field does */
    required?: boolean,
    multiple?: boolean,
    value: ApprovalFileValue[],
    onChange: (files: ApprovalFileValue[]) => void,
    error?: string,
    /** the whole record's budget, which is what the warning names */
    maxFiles?: number,
    /** how much of that budget the other fields already hold */
    usedFiles?: number,
    /** a draft request takes a different set than a completion's certificate */
    acceptedTypes?: string[],
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const inputRef = useRef<HTMLInputElement>(null);

    const [isUploading, setIsUploading] = useState(false);
    const [isOver, setIsOver] = useState(false);

    const handleFiles = useCallback(async (files: FileList | null) => {
        if (!files?.length) {
            return;
        }
        let selected = multiple ? Array.from(files) : [files[0]];
        if (maxFiles !== undefined) {
            // refuse the surplus here rather than letting the API reject the whole save
            const free = Math.max(0, maxFiles - usedFiles - value.length);
            if (selected.length > free) {
                dispatch(addMessage({severity: 'warning', title: t('approvals.file.tooMany', {max: maxFiles})}));
                selected = selected.slice(0, free);
            }
            if (!selected.length) {
                return;
            }
        }
        setIsUploading(true);
        const uploaded: ApprovalFileValue[] = [];
        for (const file of selected) {
            if (acceptedTypes.indexOf(file.type) < 0) {
                dispatch(addMessage({severity: 'warning', title: t('approvals.file.badType', {name: file.name})}));
                continue;
            }
            if (file.size > MAX_FILE_SIZE) {
                dispatch(addMessage({severity: 'warning', title: t('approvals.file.tooLarge', {name: file.name})}));
                continue;
            }
            const res = await uploadFile(dispatch, file, false);
            if (!res?.id) {
                dispatch(addMessage({severity: 'error', title: t('approvals.file.failed', {name: file.name})}));
                continue;
            }
            uploaded.push({id: res.id, file_name: file.name, size: file.size, download_url: res.download_url});
        }
        setIsUploading(false);
        if (uploaded.length) {
            onChange(multiple ? [...value, ...uploaded] : uploaded);
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [multiple, maxFiles, usedFiles, value, onChange, acceptedTypes, dispatch, t]);

    const handleRemove = useCallback((id: string) => {
        onChange(value.filter(f => f.id !== id));
    }, [value, onChange]);

    const showDropZone = (multiple || !value.length)
        && (maxFiles === undefined || usedFiles + value.length < maxFiles);

    return <div>
        {/* a hand-built field, so its label carries the MuiFormLabel colour itself */}
        <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-black)'}}>
            {label}{required && <span className={'MuiFormLabel-asterisk'}>&nbsp;*</span>}
        </Typography>
        {showDropZone && <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
                e.preventDefault();
                setIsOver(true);
            }}
            onDragLeave={() => setIsOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setIsOver(false);
                handleFiles(e.dataTransfer.files).then();
            }}
            className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-24 tw-mt-4 tw-rounded-md tw-cursor-pointer'}
            style={{
                border: '1px dashed var(--color-black-inactive)',
                background: isOver ? 'var(--color-primary15)' : 'rgba(0,0,0,0.02)'
            }}>
            {isUploading ? <CircularProgress size={24}/> : <PhotoCameraOutlined/>}
            <Typography component={'div'} sx={{fontSize: '14px'}}>{t(multiple ? 'approvals.file.dropMulti' : 'approvals.file.drop')}</Typography>
            {!!hint && <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-black-inactive)'}}>{hint}</Typography>}
        </div>}
        <input ref={inputRef} type={'file'} hidden multiple={multiple}
            accept={acceptedTypes.join(',')}
            onChange={(e) => handleFiles(e.target.files)}/>
        {value.map(file => <div key={file.id}
            className={'tw-flex tw-items-center tw-gap-8 tw-border tw-border-solid tw-rounded-md tw-p-8 tw-mt-4'}
            style={{borderColor: 'var(--color-black-inactive)'}}>
            <InsertDriveFileOutlined fontSize={'small'}/>
            {file.download_url
                ? <a href={file.download_url} target={'_blank'} rel={'noreferrer'} className={'tw-grow'}>{file.file_name}</a>
                : <span className={'tw-grow'}>{file.file_name}</span>}
            <span style={{fontSize: '12px', color: 'var(--color-black-inactive)'}}>{fileSize(file.size)}</span>
            <IconButton size={'small'} onClick={() => handleRemove(file.id)} aria-label={t('form.delete')}>
                <Close fontSize={'small'}/>
            </IconButton>
        </div>)}
        {!!error && <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-error)', paddingTop: '4px'}}>{error}</Typography>}
    </div>;
};
